import { useEffect, useMemo, useState } from 'react';
import { Box, MenuItem, Select, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { COUNTRIES, DEFAULT_COUNTRY, matchCountryByDialPrefix, isValidMobile, type CountryCode } from '../lib/countries';

function splitE164(value: string): { country: CountryCode; local: string } {
  const match = matchCountryByDialPrefix(value);
  if (match) return { country: match.code, local: value.slice(match.dial.length) };
  return { country: DEFAULT_COUNTRY, local: '' };
}

interface PhoneInputProps {
  value: string;
  onChange: (e164: string) => void;
  label?: string;
  disabled?: boolean;
  autoFocus?: boolean;
}

export default function PhoneInput({ value, onChange, label, disabled, autoFocus }: PhoneInputProps) {
  const { t } = useTranslation();
  const initial = useMemo(() => splitE164(value), [value]);
  const [country, setCountry] = useState<CountryCode>(initial.country);
  const [local, setLocal] = useState(initial.local);

  // Strip a leading 0 from the local part to handle domestic-format input
  // (e.g. JP "0801234567" → dial+801234567).
  const dial = COUNTRIES.find((c) => c.code === country)?.dial ?? '';
  const digitsRaw = local.replace(/\D/g, '');
  const digits = digitsRaw.startsWith('0') ? digitsRaw.slice(1) : digitsRaw;
  const e164 = digits ? `${dial}${digits}` : '';

  useEffect(() => {
    onChange(e164);
  }, [e164, onChange]);

  const showError = digits.length > 0 && !isValidMobile(e164);

  return (
    <Box>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Select
          value={country}
          onChange={(e) => setCountry(e.target.value as CountryCode)}
          size="medium"
          disabled={disabled}
          sx={{ minWidth: 140 }}
        >
          {COUNTRIES.map((c) => (
            <MenuItem key={c.code} value={c.code}>
              <Box component="span" sx={{ mr: 1 }}>{c.flag}</Box>
              {c.dial}
            </MenuItem>
          ))}
        </Select>
        <TextField
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          placeholder="701234567"
          fullWidth
          required
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete="tel-national"
          inputMode="numeric"
          error={showError}
          helperText={showError ? t('auth.errors.invalidPhone') : ''}
        />
      </Box>
    </Box>
  );
}
